import React, { useCallback } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import Button, { LinkButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { fg } from '@confluence/feature-gating';
import { SPACE_DIRECTORY } from '@confluence/named-routes/entry-points/routes.ts';
import {
	useCanCreateSpace,
	useCreateSpaceStoreActions,
} from '@confluence/create-space/entry-points/createSpace.ts';
import { useSmartCreationModalActions } from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore.tsx';

import starredSpacesTelescope from './assets/starred-spaces-telescope.svg';
import { StarredSpacesListLoadingSkeletons } from './LoadingSkeletons.tsx';

type StarredSpacesEmptyStateProps = {
	hasUserGeneratedSpaces: boolean;
	isRovoLLMEnabled: boolean;
	onCreateSpaceClick: () => void;
	onExploreSpacesClick: () => void;
};

type CreateSpaceEmptyStateButtonProps = {
	isRovoLLMEnabled: boolean;
	onCreateSpaceClick: () => void;
};

const styles = cssMap({
	emptyState: {
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.large'),
		width: '100%',
	},
	emptyStateContent: {
		width: '100%',
	},
	emptyStateIcon: {
		alignItems: 'center',
		display: 'flex',
		flexShrink: 0,
		height: '32px',
		justifyContent: 'center',
		overflow: 'visible',
		position: 'relative',
		width: '32px',
	},
	emptyStateIconImage: {
		display: 'block',
		left: token('space.0'),
		position: 'absolute',
		top: token('space.negative.050'),
	},
	emptyStateText: {
		flexGrow: 1,
		minWidth: 0,
	},
	emptyStateAction: {
		width: '100%',
	},
});

const i18n = defineMessages({
	emptyStateExploreSpacesDescription: {
		id: 'side-navigation.starred-spaces-list.empty-state-explore-spaces-description',
		defaultMessage: 'Star spaces to keep them here for quick access.',
		description:
			'Description of the Mohiti empty state shown when there are user-created spaces but none have been starred.',
	},
	emptyStateCreateSpaceDescription: {
		id: 'side-navigation.starred-spaces-list.empty-state-create-space-description',
		defaultMessage: 'Create a space to give your team a central home for their work.',
		description:
			'Description of the Mohiti empty state shown when there are no user-created spaces and the user can create spaces.',
	},
	createSpace: {
		id: 'side-navigation.starred-spaces-list.empty-state-create-space',
		defaultMessage: 'Create space',
		description: 'Primary action in the Mohiti empty state that opens the create space experience.',
	},
	exploreSpaces: {
		id: 'side-navigation.starred-spaces-list.empty-state-explore-spaces',
		defaultMessage: 'Explore spaces',
		description: 'Primary action in the Mohiti empty state that links to the spaces directory.',
	},
});

const CreateSpaceEmptyStateButton = ({
	isRovoLLMEnabled,
	onCreateSpaceClick,
}: CreateSpaceEmptyStateButtonProps): React.JSX.Element => {
	const intl = useIntl();
	const { openDrawer } = useCreateSpaceStoreActions();
	const { openModal } = useSmartCreationModalActions();

	const handleCreateSpaceClick = useCallback(() => {
		if (isRovoLLMEnabled && fg('aifc_space_creation_enabled')) {
			openModal({ entryPoint: 'starredSpacesEmptyState', spaceKey: '' });
		} else {
			openDrawer({ current: null }, undefined);
		}

		onCreateSpaceClick();
	}, [isRovoLLMEnabled, onCreateSpaceClick, openDrawer, openModal]);

	return (
		<Button spacing="compact" shouldFitContainer onClick={handleCreateSpaceClick}>
			{intl.formatMessage(i18n.createSpace)}
		</Button>
	);
};

export const StarredSpacesEmptyState = ({
	hasUserGeneratedSpaces,
	isRovoLLMEnabled,
	onCreateSpaceClick,
	onExploreSpacesClick,
}: StarredSpacesEmptyStateProps): React.JSX.Element => {
	const intl = useIntl();
	const { canCreateSpace, loading: canCreateSpaceLoading } = useCanCreateSpace();

	if (canCreateSpaceLoading) {
		return <StarredSpacesListLoadingSkeletons />;
	}

	const shouldShowCreateSpace = canCreateSpace && !hasUserGeneratedSpaces;

	return (
		<Box paddingBlock="space.150" paddingInline="space.150" xcss={styles.emptyState}>
			<Stack alignInline="center" space="space.100">
				<Inline
					alignBlock="center"
					space="space.200"
					shouldWrap={false}
					xcss={styles.emptyStateContent}
				>
					<Box xcss={styles.emptyStateIcon}>
						<Box
							as="img"
							alt=""
							aria-hidden="true"
							height="39"
							src={starredSpacesTelescope}
							width="41"
							xcss={styles.emptyStateIconImage}
						/>
					</Box>
					<Box xcss={styles.emptyStateText}>
						<Text color="color.text">
							<FormattedMessage
								{...(shouldShowCreateSpace
									? i18n.emptyStateCreateSpaceDescription
									: i18n.emptyStateExploreSpacesDescription)}
							/>
						</Text>
					</Box>
				</Inline>
				<Box xcss={styles.emptyStateAction}>
					{shouldShowCreateSpace ? (
						<CreateSpaceEmptyStateButton
							isRovoLLMEnabled={isRovoLLMEnabled}
							onCreateSpaceClick={onCreateSpaceClick}
						/>
					) : (
						<LinkButton
							spacing="compact"
							shouldFitContainer
							href={SPACE_DIRECTORY.toUrl()}
							onClick={onExploreSpacesClick}
						>
							{intl.formatMessage(i18n.exploreSpaces)}
						</LinkButton>
					)}
				</Box>
			</Stack>
		</Box>
	);
};
